import { ApiError } from "@/lib/queryClient";

const AUTH_ERROR_CODES = new Set([
  "TOKEN_EXPIRED",
  "INVALID_TOKEN",
  "UNAUTHORIZED",
  "KUDOZ_TOKEN_EXPIRED",
  "EATZY_TOKEN_EXPIRED",
]);

export function isAuthFamilyError(err: unknown): err is ApiError {
  if (!(err instanceof ApiError)) return false;
  // For the permissions gating query, both 401 and 403 indicate the session
  // is no longer valid for any portal surface — funnel through the unified
  // expired-session flow rather than show a "couldn't verify permissions"
  // error wall.
  if (err.status === 401 || err.status === 403) return true;
  if (err.code && AUTH_ERROR_CODES.has(err.code)) return true;
  return false;
}

/**
 * Inputs to the permission-gate decision. Pure data — no React, no hooks —
 * so the gate logic can be unit-tested in isolation.
 */
export interface PermissionGateInputs {
  /** Auth context still bootstrapping (loading saved token, etc). */
  authLoading: boolean;
  /** Auth context finished and the user has a valid local session. */
  isAuthenticated: boolean;
  /** Permissions query has no data and no in-flight fetch yet. */
  permPending: boolean;
  /** Permissions query is currently in a hard-error state. */
  permIsError: boolean;
  /** The error object from the permissions query (if any). */
  permError: unknown;
  /**
   * True iff the live permissions fetch failed but a fresh cached
   * snapshot for this user is being served as a fallback.
   */
  isUsingCache: boolean;
  /**
   * True iff the resolved permissions (live OR cached) grant the
   * required role / level for this gate.
   */
  hasAccess: boolean;
  /**
   * Task #2623 — partner-portal routes accept a local `restaurant_partners`
   * membership as an alternative to the Eatzy-wide role. When this is true the
   * gate consults the membership fields below before showing access-denied.
   */
  allowPartnerMembership?: boolean;
  /** The partner-membership probe is still in flight. */
  partnerMembershipLoading?: boolean;
  /** The user has at least one active partner membership. */
  hasPartnerMembership?: boolean;
}

/**
 * Discriminated outcomes the permission gate can render. Keeping these as
 * named cases (instead of booleans inside the component) lets us test the
 * full state machine exhaustively and makes it impossible to accidentally
 * fall back to the dead-end "Try Again" wall the legacy implementation
 * used for any non-auth error.
 */
export type PermissionGateOutcome =
  /** Auth context still loading and we don't yet know who the user is. */
  | { kind: "loading" }
  /**
   * Auth context settled and the user is NOT signed in. The component
   * should redirect to the login page (caller handles routing).
   */
  | { kind: "redirect-to-login" }
  /**
   * Permissions query is in error AND the error is unrecoverable for
   * this session — either an auth-family failure (401/403/explicit auth
   * code) or a non-auth failure with no fresh cached fallback. The
   * component must dispatch the existing `auth:expired` flow (toast +
   * redirect to login with a returnUrl) instead of showing a dead-end
   * "Try Again" wall.
   */
  | { kind: "force-logout"; reason: "auth-expired" | "no-cache-fallback" }
  /**
   * The user is authenticated and we have resolved permissions, but they
   * do NOT include the required role / level — render the access-denied
   * UI.
   */
  | { kind: "access-denied" }
  /**
   * The live permissions fetch failed but a fresh per-user cached
   * snapshot is being served. Render the protected component AND a
   * degraded-mode banner so the user knows checks may be slightly
   * stale until service recovers.
   */
  | { kind: "render-with-cache-banner" }
  /** Happy path — render the protected component. */
  | { kind: "render" };

/**
 * Pure decision function that maps the gate inputs onto a single outcome.
 * Order of evaluation matters and encodes the security contract:
 *
 *   1. Auth context still loading → spinner. Never serve cache to a
 *      session whose identity we haven't confirmed.
 *   2. Not authenticated → redirect to login.
 *   3. Auth-family error (401/403/explicit code) → force logout. Cache
 *      is NEVER served to a session whose token was rejected, even if a
 *      fresh cached snapshot exists.
 *   4. Non-auth error with no usable cache → force logout (no dead-end
 *      "Try Again" wall).
 *   5. Permissions still loading and we don't yet have anything to
 *      render (live OR cached) → spinner.
 *   6. Resolved permissions don't grant access → access denied.
 *   7. Serving cached data → render protected component + degraded
 *      banner.
 *   8. Otherwise → render protected component.
 */
export function decidePermissionGateOutcome(
  inputs: PermissionGateInputs
): PermissionGateOutcome {
  const {
    authLoading,
    isAuthenticated,
    permPending,
    permIsError,
    permError,
    isUsingCache,
    hasAccess,
    allowPartnerMembership,
    partnerMembershipLoading,
    hasPartnerMembership,
  } = inputs;

  if (authLoading) return { kind: "loading" };
  if (!isAuthenticated) return { kind: "redirect-to-login" };

  if (permIsError) {
    if (isAuthFamilyError(permError)) {
      return { kind: "force-logout", reason: "auth-expired" };
    }
    if (!isUsingCache) {
      return { kind: "force-logout", reason: "no-cache-fallback" };
    }
  }

  // Still waiting on the very first fetch and we have nothing — live or
  // cached — to display yet.
  if (permPending && !isUsingCache) return { kind: "loading" };

  if (!hasAccess) {
    // Task #2623 — partner-portal fallback. Freshly invited partners have a
    // local `restaurant_partners` membership but no Eatzy-wide role yet, so
    // the role check above misses them. Before denying, consult the
    // membership probe (only for routes that opted in via
    // allowPartnerMembership).
    if (allowPartnerMembership) {
      if (partnerMembershipLoading) return { kind: "loading" };
      if (hasPartnerMembership) {
        return isUsingCache
          ? { kind: "render-with-cache-banner" }
          : { kind: "render" };
      }
    }
    return { kind: "access-denied" };
  }
  if (isUsingCache) return { kind: "render-with-cache-banner" };
  return { kind: "render" };
}
